import React from 'react';
import { useUser } from '../contexts/UserContext';
import UpgradeModal from './UpgradeModal';

/**
 * FeatureGate Component
 *
 * Conditionally renders children based on user's feature access.
 * Shows a fallback message or component when user doesn't have access.
 *
 * Props:
 * @param {string} feature - Required. The feature code to check (e.g., 'screener', 'watchlist')
 * @param {React.ReactNode} children - Content to show when user has access
 * @param {React.ReactNode} fallback - Optional custom fallback component
 * @param {boolean} showFallback - Optional. If false, renders nothing when no access (default: true)
 *
 * @example
 * <FeatureGate feature="dashboard_builder">
 *   <DashboardBuilder />
 * </FeatureGate>
 *
 * @example With custom fallback
 * <FeatureGate feature="stock_analyzer" fallback={<CustomUpgradePrompt />}>
 *   <StockAnalyzer />
 * </FeatureGate>
 */
function FeatureGate({ feature, children, fallback, showFallback = true }) {
  const { hasFeatureAccess, loading, isAuthenticated } = useUser();

  // Show loading state while fetching user profile
  if (loading) {
    return <FeatureGateLoading />;
  }

  // If not authenticated, they'll be redirected to login by the route guard
  // But still show the fallback in case this component is used inline
  if (!isAuthenticated) {
    return showFallback ? (fallback || <FeatureDisabledMessage feature={feature} isAuthRequired />) : null;
  }

  // Check feature access
  if (hasFeatureAccess(feature)) {
    return <>{children}</>;
  }

  // No access - show fallback or nothing
  if (!showFallback) {
    return null;
  }

  return fallback || <FeatureDisabledMessage feature={feature} />;
}

/**
 * Loading state while user profile is being fetched
 */
function FeatureGateLoading() {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.spinner}></div>
      <p style={styles.loadingText}>Loading...</p>
    </div>
  );
}

/**
 * Default message shown when user doesn't have access to a feature
 */
function FeatureDisabledMessage({ feature, isAuthRequired = false }) {
  const featureNames = {
    screener: 'Options Screener',
    watchlist: 'Watchlist & Portfolio',
    dashboard_builder: 'Dashboard Builder',
    stock_analyzer: 'Stock Analyzer',
    admin_tests: 'Admin Tests',
    research: 'Research',
    scoring_builder: 'Scoring Builder',
    app_access: 'App Access',
  };

  const featureName = featureNames[feature] || feature;

  if (isAuthRequired) {
    return (
      <div style={styles.container}>
        <div style={styles.icon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>
        <h3 style={styles.title}>Authentication Required</h3>
        <p style={styles.description}>
          Please log in to access {featureName}.
        </p>
        <a href="/login" style={styles.button}>
          Log In
        </a>
      </div>
    );
  }

  // Show the non-dismissible upgrade modal with Calendly link
  return <UpgradeModal featureName={featureName} />;
}

/**
 * TierBadge Component
 *
 * Displays a visual badge showing the user's subscription tier
 *
 * @example
 * <TierBadge /> // Shows current user's tier
 * <TierBadge tier="pro" /> // Shows specific tier
 */
export function TierBadge({ tier: overrideTier }) {
  const { tier, tierDisplay, effectiveTier, isAdmin } = useUser();

  const displayTier = overrideTier || effectiveTier || tier || 'free';
  const displayName = isAdmin ? 'Admin' : (tierDisplay || displayTier);

  const tierColors = {
    free: { background: '#e9ecef', color: '#495057' },
    pro: { background: '#cce5ff', color: '#004085' },
    enterprise: { background: '#d4edda', color: '#155724' },
    admin: { background: '#f8d7da', color: '#721c24' },
  };

  const colors = tierColors[displayTier] || tierColors.free;

  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      backgroundColor: colors.background,
      color: colors.color,
    }}>
      {displayName}
    </span>
  );
}

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: '#1a1a1a',
    borderRadius: '12px',
    border: '1px solid #333',
    textAlign: 'center',
    maxWidth: '500px',
    margin: '40px auto',
  },
  icon: {
    color: '#666',
    marginBottom: '20px',
  },
  title: {
    color: '#fff',
    fontSize: '24px',
    fontWeight: 600,
    margin: '0 0 12px 0',
  },
  description: {
    color: '#999',
    fontSize: '16px',
    margin: '0 0 8px 0',
    lineHeight: 1.5,
  },
  subdescription: {
    color: '#666',
    fontSize: '14px',
    margin: '0 0 24px 0',
  },
  button: {
    display: 'inline-block',
    padding: '12px 32px',
    backgroundColor: '#218aff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #333',
    borderTopColor: '#218aff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#999',
    marginTop: '16px',
    fontSize: '14px',
  },
};

export default FeatureGate;
